/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import _get from "lodash/get"
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import {
  INVENTORY_BULK_UPLOAD_SUPPORTED_FILES,
  MENU_BULK_UPLOAD_SUPPORTED_FILES,
} from "constants/menu_bulk_upload"
import { useDispatch, useSelector } from "react-redux"
import {
  SCRIBE_BULK_UPLOAD_LOADING_STATUS,
  useScribeUploadAndPublishDrawer,
} from "pages/menu/detail/bulkUpload/scribe/useScribeUploadAndPublishDrawer"
import { actions, actionTypes } from "store/duck/bulkUpload/actions"
import { AnyAction } from "redux"
import { AxiosAction } from "types/api"
import {
  CreateBulkTaskResponse,
  UploadType,
} from "types/delv-platform-api/bulkUploadTask"
import { AppState } from "store/store.type"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { Config } from "constants/config"
import { enableBulkUploadProcessingBannerByUploadTypeValue } from "constants/menu"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useCompleteBulkTaskMutation, useCreateBulkTaskMutation } from "queries"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { useGetUploadType } from "./useGetUploadType"

export const useUploadFileBehavior = () => {
  const dispatch = useDispatch()
  const {
    bulkUploadType,
    drawerVisible,
    setDrawerState,
    setDrawerVisible,
  } = useContext(BulkUploadContext)

  const {
    flags: {
      completeBulkTask: isRtkCompleteBulkTaskEnabled,
      createBulkTask: isRtkCreateBulkTaskEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [completeBulkTaskRtk] = useCompleteBulkTaskMutation()
  const [createBulkTaskRtk] = useCreateBulkTaskMutation()

  const supportedFiles =
    bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
      ? INVENTORY_BULK_UPLOAD_SUPPORTED_FILES
      : MENU_BULK_UPLOAD_SUPPORTED_FILES

  const [selectedFile, setSelectedFile] = useState<File>()
  const [uploadFileFailedError, setUploadFileFailedError] = useState<{
    isError?: boolean
    errorMessage?: string
  }>({})
  const {
    merchantGroupID,
    merchantID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)
  const {
    trackDragReplaceFile,
    trackUploadItemsClick,
  } = useScribeUploadAndPublishDrawer()

  const textMap = useTranslationsMapTyped({
    fileNameLenErrMsg:
      "bulkUpload.notifications.filePreValidationErr.fileNameLenExceed",
  })

  useEffect(() => {
    // TODO review again when integrate with upload endpoint
    if (!drawerVisible && selectedFile) {
      setSelectedFile(undefined)
      setDrawerState(DRAWER_STATES.START)
    }
  }, [drawerVisible, selectedFile, setSelectedFile, setDrawerState])

  const validateLocalFile = useCallback(
    (file: File): string | undefined => {
      // revalidating file type here, although it should already have been validated before this
      if (
        !supportedFiles.some(supportedFile =>
          file.type?.endsWith(`/${supportedFile}`),
        )
      ) {
        // TODO update content when it's ready
        return "File type is not supported"
      }

      if (file.name.length > Config.maxLengthBulkUploadFileName) {
        // Note that BE validates including extension name, hence we're doing the same
        // https://gitlab.myteksi.net/gophers/go/-/blob/d6fd7926ea5a243f5fe444f001ee7c0c85058b31/delv/cms-bulk-upload/logic/bulktask/create_bulktask.go#L136
        return textMap.fileNameLenErrMsg.replace(
          "<x>",
          Config.maxLengthBulkUploadFileName.toString(),
        )
      }

      if (file.size > Config.maxLengthBulkUploadFileSize) {
        // TODO update content when it's ready
        return "File size should not exceed 200mb"
      }

      const commonFileNameRegex = /^[a-zA-Z0-9_\-[\]()\s]+\.(csv|zip)$/
      if (!commonFileNameRegex.test(file.name)) {
        // TODO update content when it's ready
        return "File name cannot contain invalid characters, only alphanumeric characters and [] () - _ are allowed"
      }

      return undefined
    },
    [supportedFiles],
  )

  const onSelectFileFromLocal = (e: ChangeEvent<HTMLInputElement>) => {
    const file: File = _get(e, "target.files[0]")
    if (file && file instanceof File) {
      const errorMessage = validateLocalFile(file)
      if (errorMessage) {
        setUploadFileFailedError({ isError: true, errorMessage })
        // do not return here, since we want to track failure
      } else {
        setSelectedFile(file)
        setDrawerState(DRAWER_STATES.PUBLISHABLE)
        trackDragReplaceFile(SCRIBE_BULK_UPLOAD_LOADING_STATUS.SUCCESS)
        return
      }
    }
    trackDragReplaceFile(SCRIBE_BULK_UPLOAD_LOADING_STATUS.FAILURE)
  }

  // useCallback here bc this function would be the dependency for an useEffect in FileDragNDrop
  const onDropFileFromLocal = useCallback(
    (e: DragEvent) => {
      const file = _get(e, "dataTransfer.files[0]")
      if (
        file &&
        file instanceof File &&
        supportedFiles.some(supportedFile =>
          file.type?.endsWith(`/${supportedFile}`),
        )
      ) {
        const errorMessage = validateLocalFile(file)
        if (errorMessage) {
          setUploadFileFailedError({ isError: true, errorMessage })
          // do not return here, since we want to track failure
        } else {
          setSelectedFile(file)
          setDrawerState(DRAWER_STATES.PUBLISHABLE)
          trackDragReplaceFile(SCRIBE_BULK_UPLOAD_LOADING_STATUS.SUCCESS)
          return
        }
      }
      trackDragReplaceFile(SCRIBE_BULK_UPLOAD_LOADING_STATUS.FAILURE)
    },
    [supportedFiles, trackDragReplaceFile, validateLocalFile, setDrawerState],
  )

  const handleUploadFileFailed = (errorMessage?: string) => {
    setUploadFileFailedError({
      isError: true,
      errorMessage,
    })
    setDrawerState(DRAWER_STATES.PUBLISHABLE)
    trackUploadItemsClick(SCRIBE_BULK_UPLOAD_LOADING_STATUS.FAILURE)
  }

  const uploadType = useGetUploadType() as UploadType

  const enableBulkUploadProcessingBanner =
    enableBulkUploadProcessingBannerByUploadTypeValue[uploadType]

  const shouldAddHeader =
    uploadType === UploadType.UpdateMenuSingle ||
    uploadType === UploadType.UpdateMenuGroup

  const onUploadFileToServer = async () => {
    if (isRtkFlagLoading) return

    if ((!merchantGroupID && !merchantID) || !selectedFile) return

    setDrawerState(DRAWER_STATES.PUBLISHING)

    const sendCreateBulkTaskMigrationEvent = (fromRtkQuery: boolean) => {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.createBulkTask,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkCreateBulkTaskEnabled,
          FROM_RTK_QUERY: fromRtkQuery,
          FROM_OLD_REDUX: !fromRtkQuery,
          ACTION_NAME: "createBulkTask",
        },
      })
    }

    let createBulkTaskData: CreateBulkTaskResponse | undefined
    if (isRtkCreateBulkTaskEnabled) {
      const result = await createBulkTaskRtk({
        merchantID,
        merchantGroupID,
        fileName: selectedFile.name,
        uploadType,
        menuGroupID,
      })
      sendCreateBulkTaskMigrationEvent(true)
      if ("error" in result) {
        const errorData =
          result.error && "data" in result.error
            ? (result.error.data as { message?: string })
            : undefined
        handleUploadFileFailed(errorData?.message)
        return
      }
      createBulkTaskData = result.data
    } else {
      const createBulkTaskRes = (await dispatch(
        actions.createBulkTask(
          {
            fileName: selectedFile.name,
            uploadType,
            merchantGroupID: merchantGroupID || merchantID,
            menuGroupID,
          },
          merchantID,
          merchantGroupID,
        ) as AnyAction,
      )) as AxiosAction<CreateBulkTaskResponse>
      sendCreateBulkTaskMigrationEvent(false)
      createBulkTaskData = createBulkTaskRes.payload?.data
      if (!createBulkTaskData) {
        handleUploadFileFailed(createBulkTaskRes.error?.response?.data?.message)
        return
      }
    }
    const { presignedURL, taskID, headers } = createBulkTaskData || {}

    const headersValue = (headers || []).reduce((obj, { name, value }) => {
      return {
        ...obj,
        [name]: value,
      }
    }, {})

    if (taskID && presignedURL) {
      // use localRresignedURL of if wanna test upload file at local, webpack dev proxy will replace the origin back and change request headers to avoid CORS issue
      // const localPresignedURL = presignedURL.replace(
      //   "https://stg-cms-bulk-upload.s3.ap-southeast-1.amazonaws.com",
      //   "/bulk-upload-local",
      // )
      fetch(presignedURL, {
        method: "PUT",
        body: selectedFile,
        headers: shouldAddHeader ? headersValue : undefined,
      })
        .then(async res => {
          if (res?.status === 200) {
            if (!enableBulkUploadProcessingBanner) {
              // Old behavior: Stay in PUBLISHING_ERROR_CHECK state
              setDrawerState(DRAWER_STATES.PUBLISHING_ERROR_CHECK)
            }

            // While migration flags are loading, do not fall back to old Redux: the enabled bit is
            // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
            // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
            // the trigger UI while loading so clicks are not silently dropped.
            if (isRtkCompleteBulkTaskEnabled) {
              // RTK Query path

              // 1. Dispatch pending action for backward compatibility
              dispatch({
                type: actionTypes.COMPLETE_BULK_TASK,
              })

              // 2. Call RTK Query mutation and await result
              const result = await completeBulkTaskRtk({
                merchantID,
                merchantGroupID,
                taskID,
                uploadType,
                menuGroupID,
              })

              // 3. Track RTK path
              TroyWebTracker.sendEvent({
                category: EventCategoryEng.rtkMigration,
                action: EventActionEng.completeBulkTask,
                labels: {
                  MERCHANT_ID: merchantID,
                  IS_RTK_ENABLED: isRtkCompleteBulkTaskEnabled,
                  FROM_RTK_QUERY: true,
                  FROM_OLD_REDUX: false,
                  ACTION_NAME: "completeBulkTask",
                },
              })

              // 4. Handle error case
              if ("error" in result) {
                // Dispatch fail action for backward compatibility
                dispatch({
                  type: getActionFail(actionTypes.COMPLETE_BULK_TASK),
                  payload: {
                    data: {},
                  },
                  meta: {
                    previousAction: {
                      type: actionTypes.COMPLETE_BULK_TASK,
                      payload: {
                        request: {
                          data: { taskID, uploadType, menuGroupID },
                        },
                      },
                    },
                  },
                })
                handleUploadFileFailed(_get(result, "error.data.message"))
                return
              }
              // 5. Handle success case
              // Dispatch success action for backward compatibility
              dispatch({
                type: getActionSuccess(actionTypes.COMPLETE_BULK_TASK),
                payload: {
                  data: undefined,
                },
                meta: {
                  previousAction: {
                    type: actionTypes.COMPLETE_BULK_TASK,
                    payload: {
                      request: {
                        data: { taskID, uploadType, menuGroupID },
                      },
                    },
                  },
                },
              })
            } else {
              // Old Redux path
              await dispatch(
                actions.completeBulkTask(
                  { taskID, uploadType, menuGroupID },
                  merchantID,
                  merchantGroupID,
                ) as AnyAction,
              )

              // Track old Redux path
              TroyWebTracker.sendEvent({
                category: EventCategoryEng.rtkMigration,
                action: EventActionEng.completeBulkTask,
                labels: {
                  MERCHANT_ID: merchantID,
                  IS_RTK_ENABLED: isRtkCompleteBulkTaskEnabled,
                  FROM_RTK_QUERY: false,
                  FROM_OLD_REDUX: true,
                  ACTION_NAME: "completeBulkTask",
                },
              })
            }
            trackUploadItemsClick(SCRIBE_BULK_UPLOAD_LOADING_STATUS.SUCCESS)

            if (enableBulkUploadProcessingBanner) {
              // New behavior: Close drawer after successful upload - banner will show ongoing status
              setDrawerVisible(false)
              setTimeout(() => {
                setDrawerState(DRAWER_STATES.START)
              }, 200)
            }
          } else {
            handleUploadFileFailed()
            // no UI design
          }
        })
        .catch((e: Error) => {
          console.error(e)
          handleUploadFileFailed()
        })
    } else {
      handleUploadFileFailed()
    }
  }

  return {
    onSelectFileFromLocal,
    onDropFileFromLocal,
    onUploadFileToServer,
    isRtkFlagLoading,
    selectedFileName: selectedFile?.name || "",
    uploadFileFailedError,
    clearUploadFileFailedError: () => setUploadFileFailedError({}),
  }
}
